// The only place this module talks to the RMVP engine (C4.1). Same-origin via
// functions/rmvp/[[path]].ts; RMVP_API_ORIGIN is a Pages environment variable.
import type { FreshnessMeta } from "@/lib/api";

export type Enveloped<T> = { data: T; meta: FreshnessMeta };
export type Entity = "LTD" | "OU" | "all";
export type BookEntity = Exclude<Entity, "all">;
export type EventType = "opening_balance" | "purchase_intake" | "production_pull" | "stock_take_adjustment" | "disposal" | "void";

export type PoolRow = {
  entity_id: BookEntity; xero_item_code: string; book_currency: "GBP" | "EUR";
  qty: string; value: string; avg_unit_value: string | null; inbound_value: string;
  inbound_cost_per_unit: string | null; last_unit_value_applied: string | null;
  last_inbound_cost_applied: string | null;
};
export type AvailabilityRow = { entity_id: BookEntity; xero_item_code: string; factory_id: string; qty: string };
export type OnOrderRow = {
  purchase_id: string; entity_id: BookEntity; sku: string;
  lane: "Planned" | "PO sent" | "Confirmed" | "Received";
  qty: string; received_qty: string; expected_date: string | null;
};
export type PurchaseRow = {
  purchase_id: string; entity_id: BookEntity; xero_item_code: string;
  lane: "Planned" | "PO sent" | "Confirmed" | "Received";
  qty: string; unit_price: string | null; currency: "GBP" | "EUR"; incoterm: string | null;
  xero_po_number: string | null; po_date: string | null; expected_date: string | null;
  confirmed_date: string | null; received_date: string | null; note: string | null;
  created_by: string; created_at: string; supplier_sku?: string | null; supplier_org_id?: string | null;
};
export type DocumentRow = {
  document_id: string; entity_id: BookEntity; purchase_id: string | null; xero_item_code: string | null;
  type: string; version_note: string | null; url: string; uploaded_by: string; uploaded_at: string;
};
export type PurchaseCreatePayload = {
  xero_item_code: string; supplier_org_id: string; option_id?: string | null; supplier_sku?: string | null;
  qty: string; unit_price: string; currency: "GBP" | "EUR"; incoterm?: string | null; xero_po_number?: string | null;
  po_date?: string | null; expected_date?: string | null; confirmed_date?: string | null; received_date?: string | null;
  irt_issue_ids?: string[]; note?: string | null;
};
export type PurchaseCreateOut = { purchase_id: string; status: string };
export type PurchaseMovePayload = {
  purchase_id: string; po_date?: string | null; expected_date?: string | null; confirmed_date?: string | null; received_date?: string | null;
};
export type PurchaseMoveOut = { purchase_id: string; status: string; expected_date: string | null };
export type DocumentAttachPayload = {
  purchase_id?: string | null; xero_item_code?: string | null; type: string; version_note?: string | null; url: string;
};
export type DocumentAttachOut = { document_id: string };
export type IntakeLineIn = { sku: string; invoiced_qty: string; line_value: string };
export type IntakeProposeIn = { entity: BookEntity; invoice_ref: string; freight_total: string; lines: IntakeLineIn[] };
export type IntakeLineProposal = {
  sku: string; invoice_multiplier: string; qty: string; unit_value: string; freight_share: string; inbound_cost_per_unit: string;
};
export type IntakeProposeOut = { invoice_ref: string; lines: IntakeLineProposal[] };
export type EventIn = {
  event_id: string; event_type: EventType; entity: BookEntity; sku: string; factory: string; qty: string;
  unit_value_applied?: string | null; inbound_cost_applied?: string | null; reference?: string | null;
  invoice_ref?: string | null; purchase_id?: string | null; voids_event_id?: string | null;
  qc_flag?: "pending" | "passed" | "failed" | "not_required" | null;
  freight_split?: Record<string, unknown> | null; occurred_at: string; note?: string | null;
};
export type Definition = {
  xero_item_code: string; entity_id: BookEntity; name: string;
  type: "ingredient" | "packaging"; uom: "kg" | "unit";
  level_1: string | null; level_2: string | null; level_3: string | null;
  invoice_multiplier: string; lead_time_days: number | null; moq: string | null;
  purchase_multiple: string | null; sourcing_item_id: string | null; sourcing_type: string | null;
  organic_approved: boolean | null; used_in_organic_production: boolean | null;
  barcode_gtin: string | null; length_mm: string | null; width_mm: string | null;
  height_mm: string | null; weight_g: string | null;
  factory_product_ids: Record<string, string> | null; qc_checklist: Record<string, unknown> | null;
  required_documents: string[] | null; status: "active" | "inactive";
  created_at: string; updated_at: string;
};
export type MaterialRow = { definition: Definition; changed_at: string };
export type ReconRow = {
  recon_id: string; entity_id: BookEntity; as_at_date: string; account_code: string;
  xero_total: string; pool_total: string; delta: string; verdict: "OK" | "REVIEW" | "ESCALATE";
  source: "manual" | "cron"; note: string | null; noted_by: string; noted_at: string;
};
export type ReconRecordPayload = { as_at_date: string; account_code: string; xero_total: string; pool_total: string; note?: string | null };
export type ReconRecordOut = { recon_id: string; delta: string; verdict: "OK" | "REVIEW" | "ESCALATE" };
export type EventRow = {
  event_id: string; seq: number; event_type: EventType; entity_id: BookEntity;
  xero_item_code: string; factory_id: string; qty_delta: string;
  unit_value_applied: string | null; inbound_cost_applied: string | null;
  reference: string | null; invoice_ref: string | null; purchase_id: string | null;
  voids_event_id: string | null; qc_flag: "pending" | "passed" | "failed" | "not_required" | null;
  occurred_at: string; recorded_at: string; actor: string; note: string | null;
  is_voided: boolean; voided_by: string | null;
};
export type StockTakeLineIn = {
  sku: string; factory: string; counted_qty: string;
  typed_unit_value?: string; typed_inbound_per_unit?: string;
};
export type StockTakeIn = { entity: BookEntity; count_ref: string; occurred_at: string; lines: StockTakeLineIn[] };
export type StockTakeOut = { count_ref: string; events: EventRow[] };
export type PullLineIn = { sku: string; qty: string; pull_event_id: string };
export type PullIn = {
  run_key: string;
  entity: Entity;
  factory: string;
  occurred_at: string;
  lines: PullLineIn[];
};
export type PullLineOut = {
  sku: string;
  pool_event_id: string;
  unit_value_applied: string;
  inbound_cost_per_unit: string;
};
export type PullOut = { run_key: string; lines: PullLineOut[] };
export type ValuesOut = { avg_unit_value: string | null; inbound_cost_per_unit: string | null; qty: string };
export type VoidIn = { reason: string; occurred_at: string; void_event_id?: string };
export type MaterialUpsertPayload = Omit<Definition, "entity_id" | "created_at" | "updated_at" | "invoice_multiplier" | "status" | "level_1" | "level_2" | "level_3"> & {
  invoice_multiplier?: string; status?: "active" | "inactive"; level_3?: string;
};
export type LevelRow = {
  level_1: "Ingredient" | "Packaging"; level_2: string; level_3: string;
  active: boolean; created_at: string;
};
export type CommandIn<TPayload> = {
  command_id: string;
  type: "material.upsert" | "purchase.create" | "purchase.move" | "document.attach" | "recon.record";
  entity: BookEntity; reason?: string; payload: TPayload;
};
export type MaterialUpsertOut = { xero_item_code: string; entity_id: BookEntity; updated_at: string };
export type ErrorBody = { error: string; message: string; details: Record<string, unknown> };

export class RmvpError extends Error {
  constructor(readonly status: number, readonly body: ErrorBody | null) {
    super(`RMVP API ${status}`);
  }
}

const BASE = import.meta.env.VITE_RMVP_API_BASE ?? "/rmvp/v1";
const HEADERS = { Accept: "application/json", "Content-Type": "application/json" };

async function safeBody(response: Response): Promise<ErrorBody | null> {
  try {
    const body = (await response.json()) as Partial<ErrorBody>;
    return typeof body?.error === "string"
      ? { error: body.error, message: body.message ?? "", details: body.details ?? {} }
      : null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, init: RequestInit): Promise<Enveloped<T>> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, { credentials: "include", headers: HEADERS, ...init });
  } catch {
    throw new RmvpError(0, null);
  }
  if (!response.ok) throw new RmvpError(response.status, await safeBody(response));
  return (await response.json()) as Enveloped<T>;
}

export const get = <T>(path: string) => request<T>(path, { method: "GET" });
export const post = <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) });
const qs = (params: Record<string, string | null | undefined>) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value != null && value !== "") query.set(key, value);
  const value = query.toString();
  return value ? `?${value}` : "";
};

export const getPool = (entity: Entity, asAt: string | null) => get<PoolRow[]>(`/pool${qs({ entity, as_at: asAt })}`);
export const getAvailability = (entity: Entity, factory: string | null, asAt: string | null) => get<AvailabilityRow[]>(`/availability${qs({ entity, factory, as_at: asAt })}`);
export const getOnOrder = (entity: Entity) => get<OnOrderRow[]>(`/on-order${qs({ entity })}`);
export const getPurchases = (entity: Entity, asAt: string | null) => get<PurchaseRow[]>(`/purchases${qs({ entity, as_at: asAt })}`);
export const getDocuments = (entity: Entity, filter: { purchase_id?: string; sku?: string }) =>
  get<DocumentRow[]>(`/documents${qs({ entity, purchase_id: filter.purchase_id, sku: filter.sku })}`);
export const getMaterials = (entity: Entity, asAt: string | null) => get<MaterialRow[]>(`/materials${qs({ entity, as_at: asAt })}`);
export const getLevels = () => get<LevelRow[]>("/levels");
export const getRecon = (entity: Entity) => get<ReconRow[]>(`/recon${qs({ entity })}`);
export const getLog = (entity: Entity, opts: { sku?: string | null; asAt?: string | null; limit?: number } = {}) =>
  get<EventRow[]>(`/log${qs({ entity, sku: opts.sku, as_at: opts.asAt, limit: String(opts.limit ?? 200) })}`);
export const getValues = (entity: Exclude<Entity, "all">, sku: string) =>
  get<ValuesOut>(`/values?entity=${entity}&sku=${encodeURIComponent(sku)}`);
export const postStockTake = (body: StockTakeIn) => post<StockTakeOut>("/stock-takes", body);
export const postPull = (body: PullIn) => post<PullOut>("/pulls", body);
export const postIntakePropose = (body: IntakeProposeIn) => post<IntakeProposeOut>("/intakes/propose", body);
export const postEvent = (body: EventIn) => post<EventRow>("/events", body);
export const postVoid = (eventId: string, body: VoidIn) => post<EventRow>(`/events/${eventId}/void`, body);
export const postCommand = <TPayload, TOut>(body: CommandIn<TPayload>) => post<TOut>("/commands", body);

const KNOWN: Record<string, string> = {
  no_token: "You need to sign in to use this.",
  invalid_token: "Your sign-in has expired. Reload the page to sign in again.",
  machine_unknown: "This caller is not allowed to use the pool.",
  denied: "You can view this but not change it. Ask Bojan for edit rights in Margin Engine.",
  resolver_unreachable: "The portal could not confirm your permission. Try again in a minute.",
  not_configured: "The pool service is not connected to the portal yet.",
  bad_request: "The portal could not route this request to the pool service. Reload and try again.",
  unknown_level: "That category is not on the list any more. Pick another.",
  level_type_mismatch: "That category belongs to the other material type.",
  validation_error: "The request is not valid.",
};

export function messageFor(error: unknown): string {
  if (!(error instanceof RmvpError)) return "Something went wrong. Nothing was saved.";
  if (error.status === 0) return "Couldn't reach the pool service. Nothing was saved.";
  const code = error.body?.error ?? "";
  if (error.body?.message) return error.body.message;
  return KNOWN[code] ?? `The pool service answered ${error.status}. Nothing was saved.`;
}

export function isCode(error: unknown, code: string): error is RmvpError {
  return error instanceof RmvpError && error.body?.error === code;
}
